import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import {
  CASE_STUDY_ASSIGNMENT_NEW,
  CASE_STUDY_PAYMENT_CHECKOUT_ROUTE,
} from "../../helper/Constants";
import { useNavigate } from "react-router-dom";
import * as casestudyService from "../../services/CasestudyService";
import Notification from "../../../../components/mui/Notification";
import { PageHeader } from "../../../../components";
import { useLocation } from "react-router-dom";
import NumberInput from "../../../../components/NumberInput";
import { Skeleton } from "@mui/material";
import ResponsiveDialog from "../../../../components/ResponsiveDialog";

export default function PaymentOptions() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [loading, setLoading] = useState(true);
  const [shareWithStudentAllowed, setShareWithStudentAllowed] = useState(false);
  const [options, setOptions] = useState([]);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [shareEligibilityDialog, setShareEligibilityDialog] = useState({
    isOpen: false,
  });
  const casestudy = state.casestudy;
  const institutionId = state.institutionId;
  const caseStudyId = casestudy.id;
  const course = state.course;

  useEffect(() => {
    casestudyService
      .getPaymentOptions()
      .then((response) => {
        setOptions(response.data);
      })
      .catch((e) => {
        console.error(`Error: ${e.message}`);
        setNotify({
          isOpen: true,
          message: "Could not connect to server. Please try again later.",
          type: "error",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    casestudyService
      .isUserEligibleToShare()
      .then((response) => {
        setShareWithStudentAllowed(response.data);
      })
      .catch((e) => {
        console.error(`Error: ${e.message}`);
        setNotify({
          isOpen: true,
          message: "Could not connect to server. Please try again later.",
          type: "error",
        });
      });
  }, []);

  const handleSelection = (option) => {
    if (shouldBeSharedWithStudents(option.code)) {
      if (shareWithStudentAllowed) {
        navigate(CASE_STUDY_ASSIGNMENT_NEW, {
          state: {
            casestudy,
            institutionId,
            course,
            resetPaymentStatus: true,
          },
        });
      } else {
        setShareEligibilityDialog({
          isOpen: true,
          title: "Payment Required to Share Case Study",
          content:
            "You’ve reached the maximum number of case studies allowed under the free plan. While you can continue to create new case studies, sharing them with students is restricted beyond this limit. We haven’t charged you anything yet, but to unlock sharing with students, a payment is required.",
          actions: [
            {
              label: "Close",
              onClick: () =>
                setShareEligibilityDialog({
                  ...shareEligibilityDialog,
                  isOpen: false,
                }),
            },
            {
              label: "Select another option",
              onClick: () =>
                setShareEligibilityDialog({
                  ...shareEligibilityDialog,
                  isOpen: false,
                }),
              autoFocus: true,
            },
          ],
        });
      }
    } else {
      navigate(CASE_STUDY_PAYMENT_CHECKOUT_ROUTE, {
        state: {
          caseStudyId: caseStudyId,
          quantity: option.quantity || option.min,
          optionId: option.id,
          amount: option.finalPrice || option.price,
          currency: option.currencyCode,
        },
      });
    }
  };

  const calculateFinalPrice = (opt, qty) => {
    const basePrice = parseFloat(opt.price);

    let discount = 0;

    for (const d of opt.discounts) {
      if (qty >= d.minStudents && qty <= d.maxStudents) {
        discount = d.amount;
        break;
      }
    }

    const discountedPrice = basePrice - (basePrice * discount) / 100;
    const total = qty * discountedPrice;
    return parseFloat(total.toFixed(2));
  };

  const handlePriceAndQuantity = (optIndex) => {
    const updatedOptions = [...options];
    const opt = updatedOptions[optIndex];
    const qty = opt.min;
    const total = calculateFinalPrice(opt, qty);
    opt.finalPrice = total;
    opt.quantity = qty;
    setOptions(updatedOptions);
  };

  const handleQuantityChange = (optIndex) => (event, value) => {
    const qty = Number(value);
    const updatedOptions = [...options];
    const opt = updatedOptions[optIndex];
    const total = calculateFinalPrice(opt, qty);
    opt.finalPrice = total;
    opt.quantity = qty;
    setOptions(updatedOptions);
  };

  const shouldBeSharedWithStudents = (code) => "TRAINING" === code;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader title="Case study payment" isChild={true} />
      <Container
        id="pricing"
        sx={{
          // pt: { xs: 4, sm: 12 },
          // pb: { xs: 8, sm: 16 },
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 3, sm: 6 },
        }}
      >
        <Box
          sx={{
            width: { sm: "100%", md: "60%" },
            textAlign: { sm: "left", md: "center" },
          }}
        >
          {/* <Typography
            component="h2"
            variant="h4"
            gutterBottom
            sx={{ color: "text.primary", fontWeight: "bold" }}
          >
            Pricing
          </Typography> */}
          {/* <Typography
            variant="body1"
            sx={{ color: "text.secondary" }}
            gutterBottom
          >
            Select your plan and follow the instructions to complete your
            purchase.
          </Typography> */}
        </Box>
        <Grid
          container
          spacing={3}
          sx={{
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {loading
            ? Array.from(new Array(3)).map((item, index) => (
                <Grid
                  key={index}
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  sx={{ m: 2 }}
                >
                  <Skeleton variant="rectangular" height={120} />
                  <Skeleton />
                  <Skeleton width="60%" />
                </Grid>
              ))
            : options.map((option, optIndex) => (
                <Grid
                  item
                  sx={{
                    p: 1, // Adds padding inside each tier
                    my: 2, // Adds vertical margin between tiers
                  }}
                  xs={12}
                  sm={option.title === "Gold" ? 12 : 6}
                  md={4}
                  key={option.title}
                >
                  <Card
                    sx={[
                      {
                        p: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      },
                      option.recommended
                        ? (theme) => ({
                            border: "none",
                            background:
                              "radial-gradient(circle at 50% 0%, hsla(218, 12.80%, 66.30%, 0.49), hsl(214, 6.00%, 77.10%))",
                            boxShadow: `0 8px 12px hsla(220, 24.10%, 61.80%, 0.20)`,
                          })
                        : {
                            border: "none",
                            background:
                              "radial-gradient(circle at 50% 0%, hsla(219, 34.70%, 90.40%, 0.35), hsl(220, 12.50%, 90.60%))",
                            boxShadow: `0 8px 12px hsla(219, 15.20%, 82.00%, 0.20)`,
                          },
                    ]}
                  >
                    <CardContent>
                      <Box
                        sx={[
                          {
                            // mb: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                          },
                          option.recommended ? { color: "" } : { color: "" },
                        ]}
                      >
                        <Typography component="h3" variant="h6">
                          {option.title}
                        </Typography>
                        {option.recommended && (
                          <Chip
                            icon={<AutoAwesomeIcon />}
                            label={"Recommended"}
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Box
                        sx={[
                          {
                            display: "flex",
                            alignItems: "baseline",
                          },
                          option.recommended
                            ? { color: null }
                            : { color: null },
                        ]}
                      >
                        <Typography
                          component="h3"
                          variant="h4"
                          sx={{ color: "text.primary", fontWeight: "bold" }}
                        >
                          ${option.price}
                        </Typography>
                        <Typography component="h3" variant="h6">
                          &nbsp; {option.description}
                        </Typography>
                      </Box>
                      <Divider
                        sx={{ my: 2, opacity: 0.8, borderColor: "divider" }}
                      />
                      {option.features.map((feat) => (
                        <Box
                          key={feat}
                          sx={{
                            py: 1,
                            display: "flex",
                            gap: 1.5,
                            alignItems: "center",
                          }}
                        >
                          <CheckCircleRoundedIcon
                            sx={[
                              {
                                width: 20,
                              },
                              option.recommended
                                ? { color: "primary.main" }
                                : { color: "primary.main" },
                            ]}
                          />
                          <Typography
                            variant="subtitle2"
                            component={"span"}
                            sx={[
                              option.recommended
                                ? { color: null }
                                : { color: null },
                            ]}
                          >
                            {feat}
                          </Typography>
                        </Box>
                      ))}
                      {option.discounts.map((dis) => (
                        <Box
                          key={dis.id}
                          sx={{
                            py: 1,
                            display: "flex",
                            gap: 1.5,
                            alignItems: "center",
                          }}
                        >
                          <CheckCircleRoundedIcon
                            sx={[
                              {
                                width: 20,
                              },
                              option.recommended
                                ? { color: "primary.main" }
                                : { color: "primary.main" },
                            ]}
                          />
                          <Typography
                            variant="subtitle2"
                            component={"span"}
                            sx={[
                              option.recommended
                                ? { color: null }
                                : { color: null },
                            ]}
                          >
                            {dis.amount +
                              "% " +
                              "off for " +
                              dis.minStudents +
                              "-" +
                              dis.maxStudents +
                              " students"}
                          </Typography>
                        </Box>
                      ))}
                    </CardContent>
                    <CardActions
                      sx={{
                        display: "flex",
                        flexDirection: "column", // stack children vertically
                        alignItems: "center", // center horizontally
                        justifyContent: "center",
                        gap: 2, // spacing between rows
                      }}
                    >
                      {!shouldBeSharedWithStudents(option.code) && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Typography sx={{ mb: 1 }}>Quantity</Typography>
                          <NumberInput
                            aria-label="Quantity Input"
                            min={option.min}
                            max={999}
                            defaultValue={option.min}
                            onChange={handleQuantityChange(optIndex)}
                          />
                        </Box>
                      )}
                      <Button
                        onClick={() => handleSelection(option)}
                        fullWidth
                        variant={option.recommended ? "contained" : "outlined"}
                        color={option.recommended ? "success" : "primary"}
                      >
                        {shouldBeSharedWithStudents(option.code)
                          ? "Share with students"
                          : "Proceed to Pay $" +
                            (option.finalPrice ||
                              handlePriceAndQuantity(optIndex))}
                      </Button>
                    </CardActions>

                    {/* {!shouldBeSharedWithStudents(option.code) && (
                  <Collapse in={true}>
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography sx={{ mb: 1 }}>Quantity</Typography>
                        <NumberInput
                          aria-label="Quantity Input"
                          min={1}
                          max={99}
                        />
                      </Box>
                    </CardContent>
                  </Collapse>
                )} */}
                  </Card>
                </Grid>
              ))}
        </Grid>
        <Notification notify={notify} setNotify={setNotify} />
        <ResponsiveDialog
          dialog={shareEligibilityDialog}
          setDialog={setShareEligibilityDialog}
        />
      </Container>
    </Container>
  );
}
